import React, { useEffect, useRef } from 'react';
import { Document } from '../../services/api';
import styles from './DocumentAutocomplete.module.css';

interface DocumentAutocompleteProps {
  documents: Document[];
  selectedIndex: number;
  onSelect: (doc: Document) => void;
  onSelectedIndexChange: (index: number) => void;
  position: { top: number; left: number };
}

export const DocumentAutocomplete: React.FC<DocumentAutocompleteProps> = ({
  documents,
  selectedIndex,
  onSelect,
  onSelectedIndexChange,
  position,
}) => {
  const resultListRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (resultListRef.current && itemRefs.current[selectedIndex]) {
      const selectedItem = itemRefs.current[selectedIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  if (documents.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.autocomplete}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className={styles.resultList} ref={resultListRef}>
        {documents.map((doc, index) => (
          <div
            key={doc.id}
            ref={(el) => (itemRefs.current[index] = el)}
            className={`${styles.resultItem} ${index === selectedIndex ? styles.selected : ''}`}
            onClick={() => onSelect(doc)}
            onMouseEnter={() => onSelectedIndexChange(index)}
          >
            <div className={styles.resultTitle}>{doc.title}</div>
            {doc.folder_path && (
              <div className={styles.resultPath}>{doc.folder_path}</div>
            )}
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <kbd>↑↓</kbd> Navigate · <kbd>Enter</kbd> Select · <kbd>Esc</kbd> Cancel
      </div>
    </div>
  );
};
